<template>
  <b-card
    no-body
    class="mt-2"
  >
    <b-card-header>
      <b-card-title>Attachments</b-card-title>
    </b-card-header>

    <b-card-body>
      <!-- Allocation approval: only meaningful for contracted people -->
      <div
        v-if="isExternal"
        class="mb-2 p-1 rounded"
        :class="allocationApproved ? 'bg-light-success' : 'bg-light-warning'"
      >
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div>
            <h5 class="mb-0">
              Allocation approval — {{ certificateCategoryName || 'this category' }}
            </h5>
            <small v-if="allocationApproved">
              Approved<span v-if="approverName"> by {{ approverName }}</span>
              <span v-if="approvedAt"> on {{ approvedAt }}</span> —
              this inspector can be allocated requests in this category.
            </small>
            <small v-else>
              Not approved — this inspector will not appear in allocation for this category.
            </small>
          </div>
          <b-button
            :variant="allocationApproved ? 'outline-danger' : 'success'"
            size="sm"
            :disabled="savingApproval"
            @click="toggleApproval"
          >
            {{ savingApproval ? 'Saving…' : (allocationApproved ? 'Withdraw approval' : 'Approve for allocation') }}
          </b-button>
        </div>
      </div>

      <!-- GIF-HR checklist: both items must be selected and approved for the inspector to
           show as Active for this certificate category. -->
      <div
        v-if="isExternal"
        class="mb-2"
      >
        <h5 class="mb-1">
          Inspector Checklist
          <b-badge
            :variant="isInspectorActive ? 'light-success' : 'light-secondary'"
            class="ml-1"
          >
            {{ isInspectorActive ? 'Active' : 'Inactive' }}
          </b-badge>
        </h5>

        <div
          v-for="code in checklistCodes"
          :key="code"
          class="p-1 rounded mb-1"
          :class="checklistFor(code).approved ? 'bg-light-success' : 'bg-light-warning'"
        >
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div>
              <h6 class="mb-0">
                {{ code }}
              </h6>
              <small>
                <b-form-checkbox
                  :checked="checklistFor(code).selected"
                  :disabled="checklistSaving[code]"
                  switch
                  inline
                  @change="setChecklist(code, { selected: $event })"
                >
                  Selected
                </b-form-checkbox>
                <span v-if="checklistFor(code).approved">
                  Approved<span v-if="checklistFor(code).approver && checklistFor(code).approver.name"> by {{ checklistFor(code).approver.name }}</span>
                  <span v-if="checklistFor(code).approved_at"> on {{ formatDate(checklistFor(code).approved_at) }}</span>
                </span>
                <span v-else>Not approved yet</span>
              </small>
            </div>
            <b-button
              :variant="checklistFor(code).approved ? 'outline-danger' : 'success'"
              size="sm"
              :disabled="checklistSaving[code] || !checklistFor(code).selected"
              @click="setChecklist(code, { approved: !checklistFor(code).approved })"
            >
              {{ checklistSaving[code] ? 'Saving…' : (checklistFor(code).approved ? 'Withdraw approval' : 'Approve') }}
            </b-button>
          </div>

          <!-- Files for this checklist item -->
          <b-table
            v-if="checklistAttachments(code).length"
            :items="checklistAttachments(code)"
            :fields="fields"
            responsive
            small
            class="mt-1 mb-1"
          >
            <template #cell(original_filename)="data">
              {{ data.item.original_filename || 'file' }}
            </template>
            <template #cell(created_at)="data">
              {{ formatDate(data.item.created_at) }}
            </template>
            <template #cell(actions)="data">
              <a
                v-if="data.item.file"
                :href="data.item.file.download"
                target="_blank"
                rel="noopener noreferrer"
                class="mr-1"
              >
                <feather-icon
                  icon="DownloadIcon"
                  size="16"
                />
              </a>
              <b-button
                variant="flat-danger"
                size="sm"
                :disabled="deletingId === data.item.id"
                @click="removeAttachment(data.item)"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="16"
                />
              </b-button>
            </template>
          </b-table>

          <b-row class="align-items-center mt-1">
            <b-col
              cols="12"
              md="8"
            >
              <b-form-file
                v-model="checklistFilesToUpload[code]"
                multiple
                :placeholder="`Attach ${code} file(s)`"
                :disabled="checklistUploading[code]"
              />
            </b-col>
            <b-col
              cols="12"
              md="4"
              class="mt-1 mt-md-0"
            >
              <b-button
                variant="primary"
                size="sm"
                :disabled="checklistUploading[code] || !checklistFilesToUpload[code].length"
                @click="uploadChecklistFile(code)"
              >
                {{ checklistUploading[code] ? 'Uploading…' : 'Upload' }}
              </b-button>
            </b-col>
          </b-row>
        </div>
      </div>

      <!-- Existing files (general only — checklist files are shown against their own item above) -->
      <b-table
        v-if="generalAttachments.length"
        :items="generalAttachments"
        :fields="fields"
        responsive
        small
        striped
      >
        <template #cell(original_filename)="data">
          {{ data.item.original_filename || 'file' }}
        </template>

        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>

        <template #cell(actions)="data">
          <a
            v-if="data.item.file"
            :href="data.item.file.download"
            target="_blank"
            rel="noopener noreferrer"
            class="mr-1"
          >
            <feather-icon
              icon="DownloadIcon"
              size="16"
            />
          </a>
          <b-button
            variant="flat-danger"
            size="sm"
            :disabled="deletingId === data.item.id"
            @click="removeAttachment(data.item)"
          >
            <feather-icon
              icon="TrashIcon"
              size="16"
            />
          </b-button>
        </template>
      </b-table>

      <p
        v-else
        class="text-muted mb-1"
      >
        No files attached to this evaluation yet.
      </p>

      <!-- Upload -->
      <b-row class="align-items-center">
        <b-col
          cols="12"
          md="8"
        >
          <b-form-file
            v-model="filesToUpload"
            multiple
            placeholder="Choose files to attach"
            :disabled="uploading"
          />
        </b-col>
        <b-col
          cols="12"
          md="4"
          class="mt-1 mt-md-0"
        >
          <b-button
            variant="primary"
            :disabled="uploading || !filesToUpload.length"
            @click="upload"
          >
            {{ uploading ? 'Uploading…' : 'Upload' }}
          </b-button>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'PersonnelEvaluationFiles',
  props: {
    evaluationId: {
      type: [String, Number],
      required: true,
    },
    // The whole evaluation as the details page received it.
    evaluation: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      attachments: [],
      filesToUpload: [],
      uploading: false,
      deletingId: null,
      savingApproval: false,
      allocationApproved: false,
      approverName: null,
      approvedAt: null,
      checklistCodes: ['GIF-HR-020', 'GIF-HR-026'],
      checklists: [],
      isInspectorActive: false,
      checklistFilesToUpload: { 'GIF-HR-020': [], 'GIF-HR-026': [] },
      checklistUploading: { 'GIF-HR-020': false, 'GIF-HR-026': false },
      checklistSaving: { 'GIF-HR-020': false, 'GIF-HR-026': false },
      fields: [
        { key: 'original_filename', label: 'File' },
        { key: 'created_at', label: 'Uploaded' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    // is_internal_employee is the split, never the role — several contracted inspectors
    // hold the 'engineer' role.
    isExternal() {
      return this.evaluation?.user?.is_internal_employee === 0
        || this.evaluation?.user?.is_internal_employee === false
    },
    certificateCategoryName() {
      return this.evaluation?.certificate_category?.name
    },
    // General (non-checklist) attachments only — checklist files render against their own item.
    generalAttachments() {
      return this.attachments.filter(a => !a.checklist_code)
    },
  },
  watch: {
    evaluation: {
      immediate: true,
      handler(value) {
        if (!value) return
        this.attachments = Array.isArray(value.attachments) ? value.attachments : []
        this.allocationApproved = Boolean(value.allocation_approved)
        this.approverName = value.allocation_approver?.name || null
        this.approvedAt = this.formatDate(value.allocation_approved_at)
        this.checklists = Array.isArray(value.checklists) ? value.checklists : []
        this.isInspectorActive = Boolean(value.is_active)
      },
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return null
      const parsed = new Date(value)
      return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString()
    },
    checklistFor(code) {
      return this.checklists.find(c => c.code === code) || { code, selected: false, approved: false }
    },
    checklistAttachments(code) {
      return this.attachments.filter(a => a.checklist_code === code)
    },
    setChecklist(code, payload) {
      this.$set(this.checklistSaving, code, true)
      axios.put(`personnel-evaluation/${this.evaluationId}/checklists/${code}`, payload)
        .then(res => {
          const checklist = decryptData(res.data.payload)?.data?.checklist
          if (checklist) {
            const others = this.checklists.filter(c => c.code !== code)
            this.checklists = [...others, checklist]
          }
          // Refetch is_active by recomputing from the two items we now hold — both selected
          // and approved for both codes.
          this.isInspectorActive = this.checklistCodes.every(c => {
            const item = this.checklistFor(c)
            return item.selected && item.approved
          })
          this.notify('Checklist updated')
        })
        .catch(() => {
          this.notify('Failed to update checklist')
        })
        .finally(() => {
          this.$set(this.checklistSaving, code, false)
        })
    },
    uploadChecklistFile(code) {
      const files = this.checklistFilesToUpload[code]
      if (!files || !files.length) return

      const formData = new FormData()
      files.forEach((file, index) => {
        formData.append(`files[${index}]`, file)
      })
      formData.append('checklist_code', code)

      this.$set(this.checklistUploading, code, true)
      axios.post(`personnel-evaluation/${this.evaluationId}/attachments`, formData)
        .then(res => {
          const uploaded = decryptData(res.data.payload)?.data?.attachments || []
          this.attachments = [...uploaded, ...this.attachments]
          this.$set(this.checklistFilesToUpload, code, [])
          this.notify('File uploaded successfully')
        })
        .catch(err => {
          this.notify(decryptData(err.response?.data?.payload)?.message || 'Failed to upload file')
        })
        .finally(() => {
          this.$set(this.checklistUploading, code, false)
        })
    },
    notify(message) {
      this.$toasted.show(message, { position: 'top-center', duration: 3000 })
    },
    upload() {
      if (!this.filesToUpload.length) return

      const formData = new FormData()
      this.filesToUpload.forEach((file, index) => {
        formData.append(`files[${index}]`, file)
      })

      this.uploading = true
      axios.post(`personnel-evaluation/${this.evaluationId}/attachments`, formData)
        .then(res => {
          const uploaded = decryptData(res.data.payload)?.data?.attachments || []
          this.attachments = [...uploaded, ...this.attachments]
          this.filesToUpload = []
          this.notify('Attachments uploaded successfully')
        })
        .catch(err => {
          this.notify(decryptData(err.response?.data?.payload)?.message || 'Failed to upload attachments')
        })
        .finally(() => {
          this.uploading = false
        })
    },
    removeAttachment(attachment) {
      this.deletingId = attachment.id
      axios.delete(`personnel-evaluation/${this.evaluationId}/attachments/${attachment.id}`)
        .then(() => {
          this.attachments = this.attachments.filter(a => a.id !== attachment.id)
          this.notify('Attachment deleted')
        })
        .catch(() => {
          this.notify('Failed to delete attachment')
        })
        .finally(() => {
          this.deletingId = null
        })
    },
    toggleApproval() {
      const next = !this.allocationApproved
      this.savingApproval = true

      axios.post(`personnel-evaluation/${this.evaluationId}/allocation-approval`, {
        allocation_approved: next,
      })
        .then(res => {
          const evaluation = decryptData(res.data.payload)?.data?.evaluation
          this.allocationApproved = Boolean(evaluation?.allocation_approved ?? next)
          this.approverName = evaluation?.allocation_approver?.name || null
          this.approvedAt = this.formatDate(evaluation?.allocation_approved_at)
          this.notify(this.allocationApproved ? 'Approved for allocation' : 'Approval withdrawn')
        })
        .catch(() => {
          this.notify('Failed to update allocation approval')
        })
        .finally(() => {
          this.savingApproval = false
        })
    },
  },
}
</script>
